'use client';
import { Trans } from '@lingui/react/macro';
import { cx } from '@mildang/styled-system/css';
import { fallbackPage } from '@mildang/styled-system/recipes';
import { type CSSProperties, type ReactNode } from 'react';
import ChevronLeftIcon from '@mildang/icons/react/chevron-left';
import RefreshIcon from '@mildang/icons/react/refresh';
import {
  emptyGraphicOf,
  type LegacyEmptyImageType,
  type MildangEmptyImageType,
} from '../_shared/emptyGraphics';
import { ResponsiveButton } from './ResponsiveButton';

/*
 * 풀페이지 Fallback 화면(밀당). 값은 Figma IDS Core "Fallback Page"(node 47104-76231, 컴포넌트셋 `fallback_page`).
 *
 * - 예전 이름은 `ErrorPage`. Figma 에서 `Fallback Page` 로 바뀌어 이름을 맞췄다.
 * - 그래픽은 `variant` 가 고른다. 기본은 밀당 그래픽(`mildang/empty/*`, Figma `MGL_empty`),
 *   `legacy` 는 #10113 이전 ErrorPage 의 예전 회색 그림(`legacy/empty/*`). EmptyState 의 `variant` 와 같은 축이다.
 * - educore 소비자(경기도)가 쓰는 `../ErrorScreen` 동결본과는 별개다. 이름·구조 어느 것도 공유하지 않는다.
 * - 케이스는 `type` 으로 구분한다. `type` 은 그래픽·기본 문구·기본 버튼을 고른다.
 * - 버튼은 `children` 으로 덮어쓴다(미지정 시 type 기본 버튼). 2개 이상은 `FallbackPageActions` 로 감싼다.
 * - 슬롯 스타일(레이아웃·타이포·간격)은 `fallbackPage` slot recipe(`preset/recipes/fallbackPage.ts`)가 소유한다.
 */

/**
 * FallbackPage 가 고를 수 있는 그래픽 = 밀당 빈 상태 그래픽(Figma `MGL_empty`).
 * 기본값은 type 별로 정해져 있다 — common·server = unplug, notFound = barricade, authority = lock.
 */
export type FallbackGraphicType = MildangEmptyImageType;

/** `variant="legacy"` 에서 고를 수 있는 그래픽 = 예전 회색 빈 상태 그래픽(`legacy/empty/*`). */
export type LegacyFallbackGraphicType = LegacyEmptyImageType;

/**
 * 그래픽을 고르는 브랜드.
 * - `mildang` : 밀당 그래픽(오리 캐릭터). Figma "Fallback Page" 기준.
 * - `legacy`  : 예전 회색 그림. #10113 에서 FallbackPage 로 바뀌기 전 ErrorPage 의 그림 그대로다.
 */
export type FallbackPageVariant = 'mildang' | 'legacy';

/** Figma `🗂 Type` — error_common · error_404 · error_500 · error_authority_page */
export type FallbackPageType = 'common' | 'notFound' | 'server' | 'authority';

/**
 * variant × type 별 기본 그래픽. legacy 는 mildang 과 같은 이름의 그림(barricade·lock)을 쓰고,
 * legacy 에 없는 unplug 자리(server·common)는 예전 ErrorPage(#9883) 그림인 rene 를 쓴다.
 * ⚠️ `rene` 는 밀당 마스코트다. 밀당 외 서비스는 `graphicType` 으로 바꿔 쓴다(예: barricade).
 */
export const FALLBACK_DEFAULT_GRAPHIC = {
  mildang: { common: 'unplug', notFound: 'barricade', server: 'unplug', authority: 'lock' },
  legacy: { common: 'rene', notFound: 'barricade', server: 'rene', authority: 'lock' },
} as const satisfies {
  mildang: Record<FallbackPageType, FallbackGraphicType>;
  legacy: Record<FallbackPageType, LegacyFallbackGraphicType>;
};

interface CaseContent {
  title?: ReactNode;
  description?: ReactNode;
}

// switch 망라성 가드 — FallbackPageType 에 새 케이스가 추가되면 컴파일 에러로 강제한다.
function assertNever(value: never): never {
  throw new Error(`FallbackPage: 처리되지 않은 type "${String(value)}"`);
}

// type 별 기본 문구(Figma 원문). 함수로 감싸 Trans 가 렌더 컨텍스트에서 평가되게 한다.
function getCaseContent(type: FallbackPageType): CaseContent {
  switch (type) {
    case 'notFound':
      return {
        title: <Trans id="ids.fallbackPage.notFound.title">앗! 페이지를 찾을 수 없어요</Trans>,
        description: (
          <Trans id="ids.fallbackPage.notFound.description">
            주소가 잘못되었거나, 제공되지 않는 페이지예요.{'\n'}주소를 다시 한번 확인해주세요.
          </Trans>
        ),
      };
    case 'server':
      return {
        title: <Trans id="ids.fallbackPage.error.title">앗! 오류가 발생했어요</Trans>,
        description: (
          <Trans id="ids.fallbackPage.error.description">
            잠시 서버에 문제가 있거나, 일시적인 오류일 수 있어요.{'\n'}잠시 후 다시 확인해주세요.
          </Trans>
        ),
      };
    case 'authority':
      return {
        title: <Trans id="ids.fallbackPage.authority.title">접근 권한이 없는 페이지예요</Trans>,
        description: (
          <Trans id="ids.fallbackPage.authority.description">
            허가된 권한이 필요한 공간이에요.{'\n'}이용이 필요한 경우 관리자에게 문의해 주세요.
          </Trans>
        ),
      };
    case 'common':
      return {};
    default:
      return assertNever(type);
  }
}

interface FallbackPageBaseProps {
  /** 에러 유형(그래픽·기본 문구·기본 버튼을 결정). */
  type: FallbackPageType;
  /** 제목 override (미지정 시 type 기본값) */
  title?: ReactNode;
  /** 설명 override (미지정 시 type 기본값) */
  description?: ReactNode;
  /** 버튼 영역 override (미지정 시 type 기본 버튼). 2개 이상은 `FallbackPageActions` 로 감쌈 */
  children?: ReactNode;
  /** 뒤로가기 핸들러 (notFound·authority 기본 버튼) @default window.history.back() */
  onBack?: () => void;
  /** 다시 시도하기 핸들러 (server 기본 버튼) @default window.location.reload() */
  onRetry?: () => void;
  /** 그래픽 크기 (px) @default 280 */
  graphicSize?: number;
  /** 모바일 그래픽 크기 (px) @default 210 */
  mobileGraphicSize?: number;
  /** 루트 요소에 추가되는 className. */
  className?: string;
  /** 루트 요소(<div>)에 병합되는 인라인 스타일. 그래픽 크기 변수는 덮지 않습니다. */
  style?: CSSProperties;
}

/**
 * `variant`(브랜드)마다 고를 수 있는 그래픽이 다르다 — 없는 조합은 타입에서 막는다.
 */
type FallbackPageBrandProps =
  | {
      /** 그래픽 브랜드 @default 'mildang' */
      variant?: 'mildang';
      /** 그래픽 override (미지정 시 type 기본값). `@mildang/graphics` 의 `mildang/empty/*` 중 하나 */
      graphicType?: FallbackGraphicType;
    }
  | {
      variant: 'legacy';
      /** 그래픽 override (미지정 시 type 기본값). `@mildang/graphics` 의 `legacy/empty/*` 중 하나 */
      graphicType?: LegacyFallbackGraphicType;
    };

export type FallbackPageProps = FallbackPageBaseProps & FallbackPageBrandProps;

export function FallbackPage({
  type,
  title,
  description,
  variant = 'mildang',
  graphicType,
  children,
  onBack,
  onRetry,
  graphicSize = 280,
  mobileGraphicSize = 210,
  className,
  style,
}: FallbackPageProps) {
  const content = getCaseContent(type);
  const resolvedGraphicType = graphicType ?? FALLBACK_DEFAULT_GRAPHIC[variant][type];
  const resolvedTitle = title ?? content.title;
  const resolvedDescription = description ?? content.description;
  const resolvedAction = children ?? getDefaultAction(type, { onBack, onRetry });

  const Graphic = emptyGraphicOf(variant, resolvedGraphicType);
  const classes = fallbackPage();

  const cssVars = {
    '--fallback-page-graphic': `${graphicSize}px`,
    '--fallback-page-graphic-mobile': `${mobileGraphicSize}px`,
  } as CSSProperties;

  return (
    <div className={cx(classes.root, className)} style={{ ...style, ...cssVars }}>
      <div className={classes.contentsWrapper}>
        <Graphic className={classes.graphic} aria-hidden />
        <div className={classes.textWrapper}>
          <div className={classes.text}>
            {resolvedTitle && <h1 className={classes.title}>{resolvedTitle}</h1>}
            {resolvedDescription && <p className={classes.description}>{resolvedDescription}</p>}
          </div>
          {resolvedAction}
        </div>
      </div>
    </div>
  );
}

interface DefaultActionHandlers {
  onBack?: () => void;
  onRetry?: () => void;
}

function getDefaultAction(type: FallbackPageType, handlers: DefaultActionHandlers): ReactNode {
  const { onBack, onRetry } = handlers;

  switch (type) {
    case 'server':
      return (
        <ResponsiveButton
          variant="primary"
          startIcon={<RefreshIcon />}
          onClick={onRetry ?? (() => window.location.reload())}
        >
          <Trans id="ids.fallbackPage.retry">다시 시도하기</Trans>
        </ResponsiveButton>
      );
    case 'notFound':
    case 'authority':
      return (
        <ResponsiveButton
          variant="primary"
          startIcon={<ChevronLeftIcon />}
          onClick={onBack ?? (() => window.history.back())}
        >
          <Trans id="ids.errorPage.goBack">이전 페이지로 돌아가기</Trans>
        </ResponsiveButton>
      );
    case 'common':
      return null;
    default:
      return assertNever(type);
  }
}
